import autobind from 'autobind-decorator';

import {ObjectUtils} from "./Utils";

//constructor default values.
const defaults = {
    red: 0,
    green: 0,
    blue: 0,
    hex: "#000000"
};


//private helper methods.
function getValidColorValue(value) {
    if(Number.isInteger(value)) { //if not a number lets return 0
        if(value < 0) { // if less than zero lets make this 0
            return 0;
        }  else if (value > 255) { // if greater than 255 lets return 255
            return 255;
        } else { //we're probably valid. so lets return this.
            return value;
        }
    } else {
        return 0;
    }
}
function toPaddedHexString(num, len) {
    let str = num.toString(16); //make hex
    str = "0".repeat(len - str.length) + str; //pad if we're less then the length
    return str.substring(0, len); //weird if this is needed, but if we're over the length somehow lets trim that.
}


//a simple class to make using colors and manipulating them safe and easy.
export class StreamBankColor {
    //An array of colors that we're using in the theme. Maybe this should be a dictionary so we can pull a specific color if we want by some key?
    static GetThemeColors() {
        return [
            new StreamBankColor({hex: "#010101"}), // NEAR BLACK (TOTAL)
            new StreamBankColor({hex: "#FF931E"}), // ORANGE
            new StreamBankColor({hex: "#3FA9F5"}), // BLUE
            new StreamBankColor({hex: "#7AC943"}), // LIME
            new StreamBankColor({hex: "#FF4343"}), // RED
            new StreamBankColor({hex: "#23E5CE"}), // SEAFOAM
            new StreamBankColor({hex: "#6B6BF9"}), // PURPLE
            new StreamBankColor({hex: "#bf40dd"}), // VIOLET
            new StreamBankColor({hex: "#BCA415"}), // GOLD
            // new StreamBankColor({hex: "#FF6E9E"}), // PINK - reserved for Milestones
        ];
    }

    //setters ensure the value is good before setting it. Getters check if we're set. if so we'll ship that, if not we ship zero.
    // Techincally people can access the _ values because javascript is silly, but hopefully they'll respect the underscore.
    get red() {
        return this._red || 0;
    }
    set red(_red){
        this._red = getValidColorValue(_red);
    }
    get green() {
        return this._green || 0;
    }
    set green(_green){
        this._green = getValidColorValue(_green);
    }
    get blue() {
        return this._blue || 0;
    }
    set blue(_blue){
        this._blue = getValidColorValue(_blue);
    }
    get alpha() {
        return this._alpha || 0;
    }
    set alpha(_alpha){
        this._alpha = getValidColorValue(_alpha);
    }

    constructor(_options = null) {
        // merge the options array we're passed in with the defaults
        let options = ObjectUtils.Merge(ObjectUtils.Clone(defaults), _options || {});

        //use hex over r, g, b
        if (
            options.hex !== defaults.hex &&
            options.hex.length === 7 //TODO: Support 3 digit hex codes instead of just six
        ) {
            //drop the pound sign.
            let hex = options.hex.substring(1);

            //break hex into 3 parts.
            let rHex = hex.substring(0, 2);
            let gHex = hex.substring(2, 4);
            let bHex = hex.substring(4, 6);
            let aHex = hex.substring(6, 8);

            // default to full alpha
            aHex = aHex == '' ? 'FF' : aHex;

            //parse this into decimal and safely set.
            this.red = parseInt(rHex, 16);
            this.green = parseInt(gHex, 16);
            this.blue = parseInt(bHex, 16);
            this.alpha = parseInt(aHex, 16);
        } else {
            //if hex wasn't changed lets use the r, g, b decimal values.
            this.red = options.red;
            this.green = options.green;
            this.blue = options.blue;
            this.alpha = options.alpha;
        }
    }

    @autobind
    toHex() {
        return "#" +
            toPaddedHexString(this.red, 2) +
            toPaddedHexString(this.green, 2) +
            toPaddedHexString(this.blue, 2) +
            toPaddedHexString(this.alpha, 2);
    }

    //darken by reducing r,g,b equally,
    @autobind
    darken(amount = 10) {
        let options = {
            red: this.red - amount,
            green: this.green - amount,
            blue: this.blue - amount,
            alpha: this.alpha // don't change alpha
        }

        return new StreamBankColor(options); //make chainable
    }

    //lighten by increaseing r,g,b equally.
    @autobind
    lighten(amount = 10) {
        let options = {
            red: this.red + amount,
            green: this.green + amount,
            blue: this.blue + amount,
            alpha: this.alpha // don't change alpha
        }

        return new StreamBankColor(options); //make chainable
    }

    //darken by reducing r,g,b equally,
    @autobind
    withAlpha(amount = 255) {
        let options = {
            red: this.red,
            green: this.green,
            blue: this.blue,
            alpha: amount
        }

        return new StreamBankColor(options); //make chainable
    }

    @autobind
    getAverageLightness() {
        return ((this.red + this.green + this.blue) / 3);
    }

    //check if we're true white.
    @autobind
    isWhite() {
        return this.getAverageLightness() === 255;
    }

    //check if we're true black.
    @autobind
    isBlack() {
        return this.getAverageLightness() === 0;
    }

    //check if we're lighter than a different color or an average value.
    @autobind
    isLighterThan(value) {
        let other = 0;

        if (value instanceof StreamBankColor) {
            other = value.getAverageLightness();
        } else {
            other = value;
        }

        return this.getAverageLightness() > other;
    }

    //check if we're darker than a different color or an average value.
    @autobind
    isDarkerThan(value) {
        let other = 0;

        if (value instanceof StreamBankColor) {
            other = value.getAverageLightness();
        } else {
            other = value;
        }

        return this.getAverageLightness() < other;
    }
}



